// First-party capture of Meta's `fbclid` ad-click parameter, used to build an
// `fbc` click identifier ourselves when Meta's `_fbc` cookie is absent.
//
// Why this exists: `buildMetaConversionContext()` forwards `fbc` to the
// backend's server-side Conversions API `Purchase`. Normally that value comes
// from the `_fbc` cookie, which Meta's pixel writes on the landing page. If the
// pixel is blocked, loads too slowly on the landing page, is unconfigured, or
// the cookie was cleared, the visitor reaches commit/purchase with no click ID
// and the `Purchase` loses its strongest attribution signal. So we capture
// `fbclid` from the landing URL, keep it, and build `fbc` from it at payment
// time — only when the cookie is not there. The cookie always wins.
//
// Format, per Meta's `conversions-api/parameters/fbp-and-fbc` doc (checked
// 2026-10-03):
//   - `fb.<subdomainIndex>.<creationTime>.<fbclid>`, `creationTime` in Unix
//     MILLISECONDS.
//   - Subdomain index `1` when generating the value without saving a cookie.
//   - With no `_fbc`, "use the timestamp when you first observed or received
//     this fbclid value" — the CAPTURE time, not the commit time. Hence the
//     stored `capturedAtMs` and the keep-original-timestamp rule below.
//   - No Parameter Builder "appendix" suffix: that only applies when Meta's
//     Parameter Builder library is used, and we don't use it.
//   - Meta's `_fbc` cookie lives 90 days; our captured value does too.
//
// No Vue/Nuxt imports — auto-imported by Nuxt from `app/utils/`.

import { isTrackingOptedOut } from '~/utils/tracking-consent';

// `localStorage` rather than a cookie: the value never needs to reach the
// server on a request (it travels in the commit/purchase body), and keeping it
// out of `document.cookie` avoids colliding with — or being mistaken for —
// Meta's own `_fbc`.
const STORAGE_KEY = 'arvee_meta_fbclid';

// 90 days, matching the lifetime of Meta's own `_fbc` cookie. Enforced on read.
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

// Same defensive cap as `meta-conversion-context.ts` applies to `_fbc` /
// `_fbp`. Applied to the BUILT `fbc`, not the raw fbclid, because the built
// value is what crosses the API boundary.
const MAX_VALUE_LENGTH = 512;

interface CapturedFbclid {
  fbclid: string;
  capturedAtMs: number;
}

/*
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │  THE `fbclid` IS STORED AND FORWARDED VERBATIM.                          │
 * │                                                                          │
 * │  Never hash it. Never `decodeURIComponent` it. Never change its case.    │
 * │  Never read it via `URLSearchParams` or `route.query` — both percent-    │
 * │  decode. Nor via `window.location.search` — the router rewrites it with  │
 * │  a decoded copy at startup (see `rawLandingSearch()`). Meta: fbclid "is  │
 * │  case sensitive — do not apply any modifications".                       │
 * │                                                                          │
 * │  The `fb.1.<capturedAtMs>.` prefix added by `readCapturedFbc()` is NOT   │
 * │  "reformatting" — it is Meta's documented format for building `fbc`      │
 * │  yourself when there is no `_fbc` cookie. The fbclid portion after the   │
 * │  last prefix dot is untouched.                                           │
 * │                                                                          │
 * │  Every *other* field in a Meta `user_data` object is SHA-256 hashed, so  │
 * │  a future reader will be tempted to "fix the inconsistency". Don't.      │
 * │  Hashing fails TOTALLY and SILENTLY: Meta accepts the payload, returns   │
 * │  success, matches nothing, and attribution quietly goes to zero.         │
 * │                                                                          │
 * │  `meta-conversion-context.ts` and arvee-backend's                        │
 * │  `app/Conversions/Meta/MetaClickIdentity.php` carry the same warning.    │
 * └──────────────────────────────────────────────────────────────────────────┘
 */

/**
 * The browser's `localStorage`, or `null` on the server / when unavailable.
 *
 * Referenced as a bare global rather than `window.localStorage` so callers that
 * only touch `window.location` (and tests that stub `window` with a location
 * object) are unaffected. The property read itself can throw (e.g. storage
 * disabled by browser policy), so it lives inside the try.
 */
function storage(): Storage | null {
  if (!import.meta.client) return null;
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    return null;
  }
}

function buildFbc(fbclid: string, capturedAtMs: number): string {
  return `fb.1.${capturedAtMs}.${fbclid}`;
}

/**
 * Pull the raw `fbclid` value out of a raw `location.search` string.
 *
 * Splits on `&` and takes the text after the FIRST `=` — the same first-`=`
 * rule `cookies.ts` uses — so the value is returned byte-identical: no percent
 * decoding, no `+`-to-space, no case change. First occurrence wins. Returns
 * `undefined` when absent or empty.
 */
function extractRawFbclid(search: string): string | undefined {
  const query = search.startsWith('?') ? search.slice(1) : search;
  if (query === '') return undefined;

  for (const part of query.split('&')) {
    const eqIdx = part.indexOf('=');
    if (eqIdx < 0) continue;
    if (part.slice(0, eqIdx) !== 'fbclid') continue;

    const value = part.slice(eqIdx + 1);
    // An empty string is absence, not a value — it would build `fb.1.<ts>.`.
    return value === '' ? undefined : value;
  }
  return undefined;
}

/**
 * The raw query portion of a URL string: from the first `?` up to (not
 * including) any `#`, with the leading `?`. `''` when there is no query.
 *
 * Plain string slicing, deliberately NOT `new URL(url).search`: the URL parser
 * re-serializes the query and may percent-encode characters, so its output is
 * not guaranteed byte-identical to the request. A `?` that only appears inside
 * the fragment is not a query.
 */
function rawQueryOf(url: string): string {
  const hashIdx = url.indexOf('#');
  const beforeHash = hashIdx < 0 ? url : url.slice(0, hashIdx);
  const queryIdx = beforeHash.indexOf('?');
  return queryIdx < 0 ? '' : beforeHash.slice(queryIdx);
}

/**
 * The URL of the original document request, from the Navigation Timing entry,
 * or `undefined` when the Performance API is unavailable, has no navigation
 * entry, or throws. Never throws.
 */
function navigationEntryUrl(): string | undefined {
  try {
    if (typeof performance === 'undefined') return undefined;
    if (typeof performance.getEntriesByType !== 'function') return undefined;
    const name = performance.getEntriesByType('navigation')[0]?.name;
    return typeof name === 'string' && name !== '' ? name : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The RAW query string of the landing-page request, for `captureFbclid()`.
 *
 * Why not just `window.location.search`: the Vue router normalizes the query
 * during app startup and writes it back with `history.replaceState`, which
 * percent-decodes the fbclid (`%2F` becomes `/`). Whether this plugin reads
 * `location.search` before or after that rewrite is a race — on staging, three
 * loads of the same URL stored the raw and the decoded value alternately, and
 * each flip counted as a "different fbclid" and reset `capturedAtMs`.
 *
 * The Navigation Timing entry's `name` is the URL of the original document
 * request, which `history.replaceState` never changes, so it is the
 * authoritative source of the bytes Meta sent. Falls back to
 * `window.location.search` only when that entry is missing or empty, or the
 * Performance API is unavailable or throws. Returns `''` if even that fails.
 *
 * **Never throws.**
 */
export function rawLandingSearch(): string {
  const url = navigationEntryUrl();
  if (url !== undefined) return rawQueryOf(url);

  try {
    return window.location.search;
  } catch {
    return '';
  }
}

/**
 * Parse a stored entry, returning `undefined` for anything not exactly the
 * expected shape. Never throws.
 */
function parseEntry(raw: string): CapturedFbclid | undefined {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return undefined;

    const { fbclid, capturedAtMs } = parsed as Record<string, unknown>;
    if (typeof fbclid !== 'string' || fbclid === '') return undefined;
    if (
      typeof capturedAtMs !== 'number' ||
      !Number.isSafeInteger(capturedAtMs) ||
      capturedAtMs <= 0
    ) {
      return undefined;
    }

    return { fbclid, capturedAtMs };
  } catch {
    return undefined;
  }
}

/**
 * Whether two raw fbclid values are the SAME ad click: byte-equal, or equal
 * once percent-decoded.
 *
 * Why decoded equality counts: the router's startup `history.replaceState`
 * rewrites the address bar with a decoded query (#1977). A reload then
 * requests that DECODED URL, so the navigation entry carries `.../x` where the
 * original landing carried `...%2Fx`. Without this, the reload would look like
 * a new click, overwrite the raw value and reset `capturedAtMs`.
 *
 * Decoding is COMPARISON-ONLY — consistent with the VERBATIM box above, the
 * decoded form is never stored or forwarded. `decodeURIComponent` throws on
 * malformed input (e.g. a lone `%`); if either side fails to decode, only
 * byte equality counts. Never throws.
 */
function isSameClick(a: string, b: string): boolean {
  if (a === b) return true;
  try {
    return decodeURIComponent(a) === decodeURIComponent(b);
  } catch {
    return false;
  }
}

/**
 * Remove the stored fbclid entry. Never throws.
 *
 * Called on opt-out — both at capture time and by `buildMetaConversionContext()`
 * — so a value captured before a later opt-out (the `do-not-sell` page, or GPC
 * being turned on) is discarded rather than sitting in storage.
 */
export function clearCapturedFbclid(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing to clear.
  }
}

/**
 * Capture `fbclid` from the raw landing-page query string.
 *
 * `search` must be the RAW landing query string — see the box above. Pass
 * `rawLandingSearch()`, not `window.location.search`: the router may already
 * have rewritten the latter with a decoded copy.
 * `nowMs` is the capture time recorded as the `fbc` creation time.
 *
 * - Opted out: clears any stored entry and captures nothing.
 * - Same fbclid already stored: keeps the ORIGINAL raw value and
 *   `capturedAtMs`. A reload of the landing URL must not move the creation
 *   time forward. "Same" includes equal-once-decoded — see `isSameClick()`.
 * - Different fbclid: overwrites, so a newer ad click wins — matching how
 *   Meta's pixel updates `_fbc`.
 * - Empty, or long enough that the built `fbc` would exceed the 512 cap:
 *   ignored (any existing entry is left alone).
 *
 * **Never throws.**
 */
export function captureFbclid(search: string, nowMs: number): void {
  try {
    if (isTrackingOptedOut()) {
      clearCapturedFbclid();
      return;
    }

    const fbclid = extractRawFbclid(search);
    if (fbclid === undefined) return;
    if (buildFbc(fbclid, nowMs).length > MAX_VALUE_LENGTH) return;

    const store = storage();
    if (store === null) return;

    const existingRaw = store.getItem(STORAGE_KEY);
    const existing = existingRaw === null ? undefined : parseEntry(existingRaw);
    if (existing !== undefined && isSameClick(existing.fbclid, fbclid)) return;

    const entry: CapturedFbclid = { fbclid, capturedAtMs: nowMs };
    store.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage full / blocked / private mode — attribution is best-effort.
  }
}

/**
 * The `fbc` value built from a previously captured fbclid:
 * `fb.1.<capturedAtMs>.<fbclid>`.
 *
 * Returns `undefined` — and removes the stored entry — when the entry is
 * malformed, older than 90 days, or would exceed the 512 cap. Returns
 * `undefined` when nothing is stored. Does NOT check consent; the caller
 * (`buildMetaConversionContext()`) gates on opt-out first.
 *
 * **Never throws.**
 */
export function readCapturedFbc(nowMs: number): string | undefined {
  try {
    const store = storage();
    if (store === null) return undefined;

    const raw = store.getItem(STORAGE_KEY);
    if (raw === null) return undefined;

    const entry = parseEntry(raw);
    if (
      entry === undefined ||
      nowMs - entry.capturedAtMs > TTL_MS ||
      buildFbc(entry.fbclid, entry.capturedAtMs).length > MAX_VALUE_LENGTH
    ) {
      clearCapturedFbclid();
      return undefined;
    }

    return buildFbc(entry.fbclid, entry.capturedAtMs);
  } catch {
    return undefined;
  }
}
